/* eslint-disable import/no-extraneous-dependencies */
import React, { useState, useCallback, useEffect, Suspense } from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';

// Material
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';

// Reat to print
import { useReactToPrint } from 'react-to-print';

// Actions
import {
  setOpen,
  setSecondClose,
  setSecondOpenLoading,
} from 'store/ducks/dialogs/actions';

// Types
import { ApplicationState } from 'store';

// helpers

// Contants
import IMAGES from 'constants/images';

// Components
import { pageStyle } from 'components';

// functions
import { handleDownloadXls } from './components/Dialogue/functions';

// Styles
import useStyles from './DialogueExport.styles';

// Lazy Components
const Dialogue = React.lazy(() => import('./components/Dialogue/Dialogue'));

export interface XlsHeaderInterface {
  header: string;
  key: string;
  width: number;
}
export interface XlsRowInterface {
  key: string;
  value: string;
  image: { path: string | undefined; width: number; height: number } | null;
}

interface DilogExportInterface {
  tableInstance: any;
  pdfName: string;
  csvRef?: any;
  beforePrint?: () => void;
  pdf?: boolean;
  simplePdf?: boolean;
  simplePdfData?: any;
  columnsPerPage?: number;
  isExtraLarge?: boolean;
  csv?: boolean;
  xls?: boolean;
  isLargePdf?: boolean;
  pdfFileName?: string;
  isPrinting?: boolean;
  isDialogPrint?: boolean;
  setIsPrinting?: (param: boolean) => void;
  isMounted?: 'initalState' | 'loading' | 'notLoading';
  setIsMounted?: (param: 'initalState' | 'loading' | 'notLoading') => void;
  setIsComponentMounted?: (param: any) => void;
  xlsTitle?: string;
  xlsHeader?: XlsHeaderInterface[];
  xlsRows?: Array<XlsRowInterface[]>;
  headerFontSize?: number;
  bodyFontSize?: number;
  isSpecialHeader?: boolean;
  isSpecialHeaderData?: any;
  isHorizontalPdf?: boolean;
  isExtract?: boolean;
  clientData?: { name: string; document: string; period: string };
  csvFull?: boolean;
  csvFullRef?: any;
  totalRows?: boolean;
  csvLabel?: string;
  csvFullLabel?: string;
  onExportClick?: () => void;
}

const DialogueExport: React.FC<DilogExportInterface> = ({
  tableInstance,
  pdfName,
  csvRef,
  beforePrint,
  pdf,
  simplePdf = false,
  simplePdfData,
  columnsPerPage,
  isExtraLarge,
  csv,
  xls,
  pdfFileName,
  isLargePdf = false,
  isHorizontalPdf = false,
  isPrinting,
  setIsPrinting,
  isDialogPrint,
  isMounted,
  setIsMounted,
  setIsComponentMounted,
  xlsTitle,
  xlsHeader,
  xlsRows,
  headerFontSize,
  bodyFontSize,
  isSpecialHeader = false,
  isSpecialHeaderData = null,
  isExtract,
  clientData,
  csvFull = false,
  csvFullRef,
  totalRows,
  csvLabel,
  csvFullLabel,
  onExportClick,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const reactToPrintContent = useCallback(() => tableInstance, [
    tableInstance,
    isPrinting,
  ]);

  const COLUMN_PER_PAGE_DEFAULT_SIZE = 20;

  const [xlsLoading, setXlsLoading] = useState<boolean | undefined>(undefined);

  const transportOperatorData = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorSelected,
  );
  const userFirebaseData = useSelector(
    (state: ApplicationState) => state.users.firebaseData || null,
  );
  const handlePrintPdf = useReactToPrint({
    content: reactToPrintContent,
    documentTitle: pdfName,
    onBeforePrint: () => {
      document.body.style.zoom = '70%';
    },
    onAfterPrint: () => {
      document.body.style.zoom = '100%';

      if (setIsPrinting) setIsPrinting(false);
      dispatch(setSecondClose());
    },
    pageStyle: !isHorizontalPdf ? pageStyle : '@page { size: landscape; }',
    removeAfterPrint: true,
  });

  const handlePrintLargePdf = async () => {
    const tableElement = tableInstance?.current?.querySelector(
      '.MuiTable-root',
    ) as HTMLElement;

    if (tableElement) {
      const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
        import('jspdf'),
        import('html2canvas'),
      ]);

      const { width, height } = tableElement.getBoundingClientRect();
      // eslint-disable-next-line new-cap
      const pdf = new jsPDF('l', 'pt', [width, height]);

      const canvas = await html2canvas(tableElement, {
        useCORS: true,
        scrollX: 0,
        scrollY: -window.scrollY,
        windowWidth: document.documentElement.scrollWidth,
        windowHeight: document.documentElement.scrollHeight,
        width,
        height,
      });

      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', 0, 0, width, height);
      pdf.save(pdfFileName || 'relatorio.pdf');
    }
  };

  const handleDownloadCsv = () => {
    if (csvRef) {
      csvRef.current.link.click();
    }
  };

  const handleDownloadCsvFull = () => {
    if (csvFullRef) {
      csvFullRef.current.link.click();
    }
  };

  useEffect(() => {
    if (isMounted === 'notLoading' && isPrinting) {
      dispatch(setSecondClose());
      handlePrintPdf();
    }
  }, [isMounted, isPrinting]);

  useEffect(() => {
    if (xlsLoading) {
      dispatch(
        setSecondOpenLoading({
          title: 'Gerando Xls...',
        }),
      );
    } else if (xlsLoading !== undefined) dispatch(setSecondClose());
  }, [xlsLoading]);

  return (
    <Tooltip title='Exportar' placement='top'>
      <Button
        className={classes.content}
        onClick={async () => {
          if (onExportClick) onExportClick();
          dispatch(
            setOpen({
              open: true,
              isDialogPrint,
              content: (
                <Suspense fallback='Carregando...'>
                  <Dialogue
                    pdfName={pdfName}
                    handleDownloadCsv={handleDownloadCsv}
                    handlePrintPdf={
                      isLargePdf ? handlePrintLargePdf : handlePrintPdf
                    }
                    handleDownloadXls={() => {
                      setXlsLoading(true);
                      handleDownloadXls(
                        () => setXlsLoading(false),
                        simplePdfData.filters,
                        userFirebaseData,
                        transportOperatorData,
                        xlsTitle,
                        xlsHeader,
                        xlsRows,
                        isSpecialHeader,
                        isSpecialHeaderData,
                        isExtract,
                        clientData,
                      );
                    }}
                    beforePrint={beforePrint}
                    setIsPrinting={setIsPrinting}
                    simplePdf={simplePdf || false}
                    simplePdfData={simplePdfData}
                    columnsPerPage={
                      isExtraLarge
                        ? columnsPerPage
                        : COLUMN_PER_PAGE_DEFAULT_SIZE
                    }
                    headerFontSize={headerFontSize}
                    bodyFontSize={bodyFontSize}
                    isExtract={isExtract}
                    pdf={pdf || false}
                    csv={csv || false}
                    xls={xls || false}
                    csvFull={csvFull || false}
                    handleDownloadCsvFull={handleDownloadCsvFull}
                    totalRows={totalRows}
                    csvLabel={csvLabel}
                    csvFullLabel={csvFullLabel}
                  />
                </Suspense>
              ),
            }),
          );
        }}>
        <img src={IMAGES.REPORTS_ICONS.DOWNLOAD} alt='download' />
      </Button>
    </Tooltip>
  );
};

export default DialogueExport;
export { DialogueExport };
